import { useState } from "react";
import Card from "@mui/material/Card";
import Drawer from "@mui/material/Drawer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Grid from "@mui/material/Grid";

function SBICoreCard({ recommendations }) {
  const [phaseFilter, setPhaseFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);

  const handlePhaseChange = (event, newPhase) => {
    if (newPhase !== null) {
      setPhaseFilter(newPhase);
    }
  };

  const handleTypeChange = (event, newType) => {
    if (newType !== null) {
      setTypeFilter(newType);
    }
  };

  const handleTileClick = (recommendation) => {
    setSelectedRecommendation(recommendation);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedRecommendation(null);
  };

  const filteredRecommendations = recommendations.filter((rec) => {
    const phaseMatch = phaseFilter === "All" || rec.phase === phaseFilter;
    const typeMatch = typeFilter === "All" || rec.type === typeFilter;
    return phaseMatch && typeMatch;
  });

  const getPhaseColor = (phase) => {
    switch (phase) {
      case "Pre":
        return "#6BCF7F";
      case "Intra":
        return "#4ECDC4";
      case "Post":
        return "#FFD700";
      default:
        return "#95A5A6";
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "Guideline":
        return "description";
      case "Tool":
        return "build";
      case "Checklist":
        return "checklist";
      default:
        return "info";
    }
  };

  return (
    <>
      <Card
        sx={(theme) => ({
          height: "100%",
          borderRadius: theme.borders.borderRadius.lg,
          boxShadow: theme.boxShadows.md,
          backgroundColor: theme.functions.rgba(theme.palette.background.default, 0.8),
        })}
      >
        <MDBox p={3}>
          <MDBox display="flex" alignItems="center" mb={3}>
            <Icon sx={{ fontSize: "1.5rem", mr: 1, color: "text" }}>apps</Icon>
            <MDTypography variant="h5" fontWeight="bold" color="dark">
              SBI Core
            </MDTypography>
            <MDTypography variant="body2" color="text" ml={1}>
              ({recommendations.length} core recommendations)
            </MDTypography>
          </MDBox>

          {/* Filters */}
          <MDBox mb={3}>
            <MDBox display="flex" gap={2} flexWrap="wrap">
              <MDBox>
                <MDTypography variant="caption" color="text" fontWeight="medium" mb={0.5}>
                  Phase
                </MDTypography>
                <ToggleButtonGroup
                  value={phaseFilter}
                  exclusive
                  onChange={handlePhaseChange}
                  size="small"
                  sx={{
                    "& .MuiToggleButton-root": {
                      px: 1.5,
                      py: 0.5,
                      fontSize: "0.75rem",
                      border: "1px solid rgba(0,0,0,0.12)",
                      "&.Mui-selected": {
                        backgroundColor: "rgba(26, 77, 77, 0.1)",
                        color: "#1a4d4d",
                      },
                    },
                  }}
                >
                  <ToggleButton value="All">All</ToggleButton>
                  <ToggleButton value="Pre">Pre</ToggleButton>
                  <ToggleButton value="Intra">Intra</ToggleButton>
                  <ToggleButton value="Post">Post</ToggleButton>
                </ToggleButtonGroup>
              </MDBox>

              <MDBox>
                <MDTypography variant="caption" color="text" fontWeight="medium" mb={0.5}>
                  Type
                </MDTypography>
                <ToggleButtonGroup
                  value={typeFilter}
                  exclusive
                  onChange={handleTypeChange}
                  size="small"
                  sx={{
                    "& .MuiToggleButton-root": {
                      px: 1.5,
                      py: 0.5,
                      fontSize: "0.75rem",
                      border: "1px solid rgba(0,0,0,0.12)",
                      "&.Mui-selected": {
                        backgroundColor: "rgba(26, 77, 77, 0.1)",
                        color: "#1a4d4d",
                      },
                    },
                  }}
                >
                  <ToggleButton value="All">All</ToggleButton>
                  <ToggleButton value="Guideline">Guideline</ToggleButton>
                  <ToggleButton value="Tool">Tool</ToggleButton>
                  <ToggleButton value="Checklist">Checklist</ToggleButton>
                </ToggleButtonGroup>
              </MDBox>
            </MDBox>
          </MDBox>

          {/* Recommendation Tiles */}
          {filteredRecommendations.length === 0 ? (
            <MDBox textAlign="center" py={4}>
              <Icon sx={{ fontSize: "3rem", color: "text", mb: 2 }}>search_off</Icon>
              <MDTypography variant="h6" color="text">
                No matches
              </MDTypography>
            </MDBox>
          ) : (
            <Grid container spacing={1.5}>
              {filteredRecommendations.map((rec) => (
                <Grid item xs={6} sm={4} md={3} key={rec.id}>
                  <MDBox
                    p={2}
                    borderRadius="md"
                    sx={(theme) => ({
                      backgroundColor: "#F5F5F5",
                      border: `1px solid ${getPhaseColor(rec.phase)}40`,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: theme.boxShadows.sm,
                        backgroundColor: `${getPhaseColor(rec.phase)}10`,
                      },
                    })}
                    onClick={() => handleTileClick(rec)}
                  >
                    <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                      <Icon sx={{ fontSize: "1.25rem", color: getPhaseColor(rec.phase) }}>
                        {getTypeIcon(rec.type)}
                      </Icon>
                      <Chip
                        label={rec.phase}
                        size="small"
                        sx={{
                          backgroundColor: getPhaseColor(rec.phase),
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "0.65rem",
                        }}
                      />
                    </MDBox>
                    <MDTypography variant="body2" fontWeight="bold" color="dark" noWrap>
                      {rec.title}
                    </MDTypography>
                    <MDTypography variant="caption" color="text" fontSize="0.7rem">
                      {rec.type}
                    </MDTypography>
                  </MDBox>
                </Grid>
              ))}
            </Grid>
          )}
        </MDBox>
      </Card>

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: 400 },
            p: 3,
          },
        }}
      >
        {selectedRecommendation && (
          <MDBox>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <MDTypography variant="h5" fontWeight="bold" color="dark">
                {selectedRecommendation.title}
              </MDTypography>
              <IconButton onClick={handleCloseDrawer}>
                <Icon>close</Icon>
              </IconButton>
            </MDBox>

            <MDBox mb={2}>
              <Chip
                label={selectedRecommendation.phase}
                sx={{
                  backgroundColor: getPhaseColor(selectedRecommendation.phase),
                  color: "white",
                  fontWeight: "bold",
                  mr: 1,
                }}
              />
              <Chip
                label={selectedRecommendation.type}
                sx={{
                  backgroundColor: "#E0E0E0",
                  color: "#1a4d4d",
                  fontWeight: "medium",
                }}
              />
            </MDBox>

            <MDBox mb={3}>
              <MDTypography variant="h6" fontWeight="bold" color="dark" mb={1}>
                Summary
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {selectedRecommendation.summary}
              </MDTypography>
            </MDBox>

            <MDBox>
              <MDTypography variant="h6" fontWeight="bold" color="dark" mb={1}>
                Implementation Notes
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {selectedRecommendation.implementation}
              </MDTypography>
            </MDBox>
          </MDBox>
        )}
      </Drawer>
    </>
  );
}

export default SBICoreCard;
